/**
 * 흐름장(flow field).
 *
 * 유닛마다 A*를 돌리면 수백 마리에서 바로 죽는다. 목표 지점 한 곳을 기준으로
 * 격자 전체의 거리장을 한 번 만들고, 모든 유닛이 자기가 선 칸의 방향 벡터만 읽는다.
 * 비용은 유닛 수와 무관하게 O(격자)이고, 목표 칸이 바뀔 때만 다시 계산한다.
 *
 * 다익스트라를 다이얼 버킷 큐로 돌린다. 간선 비용 종류가 적어 힙보다 빠르고,
 * 이웃 순서가 고정이라 같은 입력이면 항상 같은 장이 나온다. 비용은 평지 기준
 * 직선 10 / 대각 14이고, 거친 지형은 이동 속도의 역수만큼 더 문다.
 */
import { FP_ONE } from './fixed.ts';
import { Cell, MOVE_PCT, TerrainGrid } from './grid.ts';

const COST_STRAIGHT = 10;
const COST_DIAG = 14;

/**
 * 지형별 간선 비용. 들어가는 칸의 지형으로 정한다.
 *
 * 이게 없으면 부대가 늪을 그대로 가로지른다. 흐름장은 거리만 보고 방향을
 * 정하므로, 느린 칸이 비싸다는 것을 여기서 알려주지 않으면 지형이 이동
 * 속도만 깎고 길 선택에는 아무 영향을 못 준다. 돌아가는 편이 나은 자리에서
 * 돌아가야 지형이 전술이 된다.
 *
 * **`MOVE_PCT`에서 뽑는다.** 처음에는 손으로 적었는데, 역수 관계인 표가 두
 * 곳에 있으면 한쪽만 고쳤을 때 길찾기와 실제 속도가 조용히 어긋난다. 실제로
 * 손으로 적은 값들이 반올림 기준조차 제각각이었다(숲 대각은 내림, 언덕
 * 대각은 올림). 이제 속도를 고치면 비용이 따라 움직인다.
 */
function terrainCosts(): Record<Cell, readonly [number, number]> {
  const out = {} as Record<Cell, readonly [number, number]>;
  for (const key of Object.keys(MOVE_PCT) as unknown as Cell[]) {
    const cell = Number(key) as Cell;
    const pct = MOVE_PCT[cell];
    out[cell] = [
      Math.round((COST_STRAIGHT * 100) / pct),
      Math.round((COST_DIAG * 100) / pct),
    ];
  }
  return out;
}

const TERRAIN_COST = terrainCosts();

/** 가장 비싼 간선. 다이얼 큐 슬롯 수가 이보다 커야 한다. */
export const MAX_EDGE_COST = Math.max(...Object.values(TERRAIN_COST).map((c) => c[1]));

/** 2의 거듭제곱으로 올린다. `%` 대신 비트 마스크를 쓸 수 있고 여유도 생긴다. */
function bucketCount(minimum: number): number {
  let n = 16;
  while (n <= minimum) n *= 2;
  return n;
}

/**
 * 다이얼 큐 슬롯 수. 최대 간선 비용보다 **커야** 한다.
 *
 * 슬롯 b를 처리하는 동안 넣는 항목의 거리는 d+1에서 d+최대비용 사이다.
 * 슬롯 수가 최대 비용보다 크면 그 범위가 b를 다시 덮지 않으므로, 이미 비운
 * 슬롯에 떨어지는 일이 없다. 지형 비용을 넣으면서 16에서 32가 됐다. 16
 * 그대로 두면 비용 20짜리 간선이 한 바퀴를 넘겨 이미 확정된 슬롯에 떨어지고,
 * 거리장이 조용히 틀어진다. 막히지도 않고 눈에 띄지도 않는다.
 *
 * 그래서 손으로 적지 않고 비용에서 뽑는다. 지형을 더 느리게 만들면 슬롯이
 * 따라 는다. `tests/terrain.test.ts`가 기준 다익스트라와 대조해 이 관계가
 * 깨졌는지 본다.
 */
const BUCKETS = bucketCount(MAX_EDGE_COST);
/** 흐름장이 닿지 못한 칸의 거리. 월드가 앞뒤를 가릴 때도 이 값을 본다. */
export const UNREACHABLE = 0x7fffffff;

/** 8방향 (dx, dy, cost). 순서 고정 — 동점 처리의 결정론이 여기 달려 있다. */
const NEIGHBORS: readonly (readonly [number, number, number])[] = [
  [0, -1, COST_STRAIGHT],
  [1, 0, COST_STRAIGHT],
  [0, 1, COST_STRAIGHT],
  [-1, 0, COST_STRAIGHT],
  [1, -1, COST_DIAG],
  [1, 1, COST_DIAG],
  [-1, 1, COST_DIAG],
  [-1, -1, COST_DIAG],
];

/** 대각 성분 길이: FP_ONE / sqrt(2) = 181 */
const DIAG_FP = 181;

export class FlowField {
  private readonly grid: TerrainGrid;
  readonly dist: Int32Array;
  readonly dirX: Int16Array;
  readonly dirY: Int16Array;
  private targetCx = -1;
  private targetCy = -1;
  private buckets: Int32Array[] = [];
  private readonly bucketLen = new Int32Array(BUCKETS);
  private pending = 0;

  constructor(grid: TerrainGrid) {
    this.grid = grid;
    const n = grid.cols * grid.rows;
    this.dist = new Int32Array(n);
    this.dirX = new Int16Array(n);
    this.dirY = new Int16Array(n);
    const cap = Math.max(64, Math.ceil(n / 4));
    for (let i = 0; i < BUCKETS; i++) this.buckets.push(new Int32Array(cap));
  }

  get target(): readonly [number, number] {
    return [this.targetCx, this.targetCy];
  }

  /** 목표 칸이 그대로면 재계산을 건너뛴다. */
  needsRecompute(cx: number, cy: number): boolean {
    return cx !== this.targetCx || cy !== this.targetCy;
  }

  private push(slot: number, cell: number): void {
    let b = this.buckets[slot];
    const len = this.bucketLen[slot];
    if (len === b.length) {
      const grown = new Int32Array(b.length * 2);
      grown.set(b);
      this.buckets[slot] = grown;
      b = grown;
    }
    b[len] = cell;
    this.bucketLen[slot] = len + 1;
    this.pending++;
  }

  /** 거리장과 큐를 비운다. 목표를 새로 잡는 셋(`compute`·`computeRow`·`computeCells`)이 같이 쓴다. */
  private reset(): void {
    this.dist.fill(UNREACHABLE);
    this.bucketLen.fill(0);
    this.pending = 0;
    this.dirX.fill(0);
    this.dirY.fill(0);
  }

  compute(targetCx: number, targetCy: number): void {
    const g = this.grid;
    const { cols } = g;
    this.targetCx = targetCx;
    this.targetCy = targetCy;
    this.reset();

    if (!g.inBounds(targetCx, targetCy)) return;

    // 목표가 벽이면 가장 가까운 열린 칸으로 밀어낸다.
    let tx = targetCx;
    let ty = targetCy;
    if (g.isBlocked(tx, ty)) {
      let found = false;
      for (let r = 1; r <= 8 && !found; r++) {
        for (let dy = -r; dy <= r && !found; dy++) {
          for (let dx = -r; dx <= r && !found; dx++) {
            const nx = targetCx + dx;
            const ny = targetCy + dy;
            if (g.inBounds(nx, ny) && !g.isBlocked(nx, ny)) {
              tx = nx;
              ty = ny;
              found = true;
            }
          }
        }
      }
      if (!found) return;
    }

    const start = ty * cols + tx;
    this.dist[start] = 0;
    this.push(0, start);
    this.propagate();
  }

  /**
   * 한 줄 전체를 목표로 삼는다. 후퇴가 쓴다 — 가장자리 어디로 빠져나가든 된다.
   *
   * 한 점을 목표로 두면 전 부대가 가장자리의 그 한 점으로 몰려 좁은 문을
   * 만든다. 바로 아래가 바위인 병사도 옆으로 돌아 가장 가까운 열린 칸을 찾게,
   * 줄의 열린 칸을 모두 거리 0에서 출발시킨다. 칸 순서가 고정이라 답이 하나다.
   */
  computeRow(targetCy: number): void {
    const g = this.grid;
    // 한 점 목표와 섞여 재계산 판단이 틀리지 않게 목표를 줄 밖의 값으로 둔다.
    this.targetCx = -2;
    this.targetCy = targetCy;
    this.reset();
    if (targetCy < 0 || targetCy >= g.rows) return;
    for (let cx = 0; cx < g.cols; cx++) {
      if (g.isBlocked(cx, targetCy)) continue;
      const c = targetCy * g.cols + cx;
      this.dist[c] = 0;
      this.push(0, c);
    }
    this.propagate();
  }

  /**
   * 여러 칸을 모두 목표로 삼는다(거리 0에서 함께 출발). 각 칸은 **가장 가까운** 목표 쪽을 가리킨다.
   *
   * 공격과 적군의 흐름장이 쓴다. 처음에는 상대 군대의 무게중심 한 점을 목표로 삼았는데, 그러면 모든 흐름선이
   * 그 한 점으로 모여 양쪽이 종대로 줄지어 부딪쳤다 — 접전 폭이 군대 폭의 절반에도 못 미쳤다(플레이한 사람이
   * 짚었다: "너무 종대로 전투가 진행돼. 적도 줄지어 내려오고"). 상대 병사가 선 칸을 목표로 두면 왼쪽 날개는
   * 왼쪽의 적에게, 오른쪽 날개는 오른쪽의 적에게 가 넓게 부딪친다. 얼마나 넓게 둘지는 월드가 고른다
   * (`FRONT_RADIUS_CELLS`).
   *
   * `cells`는 격자 색인. 중복은 한 번만 넣는다. 넣는 순서가 고정이면(유닛 색인 오름차순) 답이 하나다.
   * 목표 표시(`target`)는 (-3, 목표 칸 목록의 지문)이다. 목록이 같으면 장도 같으므로 다시 굽지 않는다.
   *
   * **열린 목표가 하나도 없으면 첫 칸을 한 점 목표로 굽는다**(`compute` — 바위면 가장 가까운 열린 칸으로 밀어낸다).
   * 처음에는 그냥 건너뛰어, 무게중심 칸이 바위이고 그 둘레에 병사가 없을 때(두 무리로 갈라진 군대) 장 전체가
   * 닿을 수 없음이 되어 표적 없는 적이 전부 섰다(리뷰가 짚었다 — 3초 동안 0px, 예전에는 190px).
   */
  computeCells(cells: Int32Array, n: number): void {
    const g = this.grid;
    const size = g.cols * g.rows;
    // FNV-1a. 칸 색인을 순서대로 섞는다.
    let h = 0x811c9dc5;
    for (let k = 0; k < n; k++) h = Math.imul(h ^ cells[k], 0x01000193);
    h |= 0;
    if (this.targetCx === -3 && this.targetCy === h) return;
    this.targetCx = -3;
    this.targetCy = h;
    this.reset();
    let seeds = 0;
    for (let k = 0; k < n; k++) {
      const c = cells[k];
      if (c < 0 || c >= size || this.dist[c] === 0) continue;
      const cx = c % g.cols;
      if (g.isBlocked(cx, (c - cx) / g.cols)) continue;
      this.dist[c] = 0;
      this.push(0, c);
      seeds++;
    }
    if (seeds === 0) {
      if (n > 0 && cells[0] >= 0 && cells[0] < size) {
        const cx = cells[0] % g.cols;
        this.compute(cx, (cells[0] - cx) / g.cols);
      }
      // 한 점 목표 표시를 지문으로 되돌린다. 같은 목록이 또 오면 건너뛴다.
      this.targetCx = -3;
      this.targetCy = h;
      return;
    }
    this.propagate();
  }

  private propagate(): void {
    const g = this.grid;
    const { cols, rows } = g;
    // 다이얼 큐: base를 1씩 올리며 슬롯을 순회한다. 슬롯 수가 최대 간선
    // 비용보다 크므로 삽입은 항상 현재 슬롯보다 앞쪽(같은 사이클 내)에 떨어진다.
    for (let base = 0; this.pending > 0; base++) {
      const b = base % BUCKETS;
      const len = this.bucketLen[b];
      if (len === 0) continue;
      const bucket = this.buckets[b];
      this.bucketLen[b] = 0;
      this.pending -= len;
      for (let k = 0; k < len; k++) {
        const cur = bucket[k];
        const d = this.dist[cur];
        if (d % BUCKETS !== b) continue; // 더 짧은 경로로 갱신된 낡은 항목
        const cx = cur % cols;
        const cy = (cur - cx) / cols;
        for (let n = 0; n < 8; n++) {
          const nb = NEIGHBORS[n];
          const nx = cx + nb[0];
          const ny = cy + nb[1];
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          if (g.isBlocked(nx, ny)) continue;
          // 대각 이동은 두 직교 칸이 모두 열려 있어야 허용(모서리 끼임 방지).
          if (nb[2] === COST_DIAG && (g.isBlocked(cx + nb[0], cy) || g.isBlocked(cx, cy + nb[1]))) continue;
          const ni = ny * cols + nx;
          // 비용은 들어가는 칸의 지형이 정한다. 나오는 칸이 아니다.
          const cost = TERRAIN_COST[g.cellAt(nx, ny)][nb[2] === COST_DIAG ? 1 : 0];
          const nd = d + cost;
          if (nd < this.dist[ni]) {
            this.dist[ni] = nd;
            this.push(nd % BUCKETS, ni);
          }
        }
      }
    }

    this.buildDirections();
  }

  /** 각 칸에서 거리가 가장 작은 이웃 방향을 고정소수점 단위 벡터로 굳혀 둔다. */
  private buildDirections(): void {
    const g = this.grid;
    const { cols, rows } = g;
    for (let cy = 0; cy < rows; cy++) {
      for (let cx = 0; cx < cols; cx++) {
        const i = cy * cols + cx;
        const here = this.dist[i];
        if (here === UNREACHABLE || here === 0) continue;
        let best = here;
        let bx = 0;
        let by = 0;
        for (let n = 0; n < 8; n++) {
          const nb = NEIGHBORS[n];
          const nx = cx + nb[0];
          const ny = cy + nb[1];
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          const nd = this.dist[ny * cols + nx];
          if (nd < best) {
            best = nd;
            bx = nb[0];
            by = nb[1];
          }
        }
        if (bx !== 0 && by !== 0) {
          this.dirX[i] = bx * DIAG_FP;
          this.dirY[i] = by * DIAG_FP;
        } else {
          this.dirX[i] = bx * FP_ONE;
          this.dirY[i] = by * FP_ONE;
        }
      }
    }
  }

  /** 해당 칸의 흐름 방향을 out[0], out[1]에 넣는다. 도달 불가면 (0,0). */
  sample(cx: number, cy: number, out: Int32Array): void {
    if (!this.grid.inBounds(cx, cy)) {
      out[0] = 0;
      out[1] = 0;
      return;
    }
    const i = cy * this.grid.cols + cx;
    out[0] = this.dirX[i];
    out[1] = this.dirY[i];
  }

  isReachable(cx: number, cy: number): boolean {
    if (!this.grid.inBounds(cx, cy)) return false;
    return this.dist[cy * this.grid.cols + cx] !== UNREACHABLE;
  }
}
